import React, { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExpandAlt } from "@fortawesome/free-solid-svg-icons";
import FullTextEditPopup from "../FullTextEditPopup";
import { HighlightChars } from "../HighlightChars";
import { useUpdateTranslation } from "../../queries/use-update-translation";
import { useGridNavigation } from "../../context/GridNavigationContext";

const isMac =
  typeof navigator !== "undefined" &&
  /mac|iphone|ipad|ipod/i.test(navigator.platform || navigator.userAgent || "");
const shortcutLabel = isMac ? "⌘M" : "Ctrl+M";

const TranslationInput = ({
  projectId,
  keyId,
  version,
  id,
  value,
  editable,
  search,
  rowIndex,
  colIndex,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [text, setText] = useState(value);
  const [showPopup, setShowPopup] = useState(false);
  const inputRef = useRef(null);
  const readOnlyRef = useRef(null);
  const skipBlurSaveRef = useRef(false);
  const isEditingRef = useRef(isEditing);

  const { mutate } = useUpdateTranslation(projectId, version);
  const nav = useGridNavigation();

  const hasCoords = rowIndex != null && colIndex != null;
  const cellId = hasCoords ? `${rowIndex}-${colIndex}` : null;

  useEffect(() => {
    isEditingRef.current = isEditing;
  }, [isEditing]);

  useEffect(() => {
    setText(value);
  }, [value]);

  useEffect(() => {
    if (isEditing && !showPopup && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing, showPopup]);

  useEffect(() => {
    if (!cellId) return undefined;
    nav.register(cellId, {
      rowIndex,
      colIndex,
      editable,
      focus: () => {
        if (isEditingRef.current && inputRef.current) {
          inputRef.current.focus();
          inputRef.current.select();
        } else if (readOnlyRef.current) {
          readOnlyRef.current.focus();
        }
      },
    });
    return () => nav.unregister(cellId);
  }, [cellId, rowIndex, colIndex, editable, nav]);

  const save = () => {
    skipBlurSaveRef.current = true;
    if (text !== value) {
      mutate({ text, keyId, translationId: id });
    }
    setShowPopup(false);
    setIsEditing(false);
  };

  const cancel = () => {
    skipBlurSaveRef.current = true;
    setText(value);
    setShowPopup(false);
    setIsEditing(false);
  };

  const openPopup = () => {
    skipBlurSaveRef.current = true;
    setShowPopup(true);
  };

  const handleKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "m") {
      e.preventDefault();
      openPopup();
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      save();
    } else if (e.key === "Escape") {
      e.preventDefault();
      cancel();
    } else if (e.key === "Tab" && cellId) {
      e.preventDefault();
      nav.moveFocus(e.shiftKey ? "left" : "right", cellId);
    } else if (e.key === "ArrowDown" && cellId) {
      e.preventDefault();
      nav.moveFocus("down", cellId);
    } else if (e.key === "ArrowUp" && cellId) {
      e.preventDefault();
      nav.moveFocus("up", cellId);
    }
  };

  const handleBlur = () => {
    if (skipBlurSaveRef.current) {
      skipBlurSaveRef.current = false;
      return;
    }
    save();
  };

  const preview = value ? (
    <HighlightChars search={search} str={value?.replaceAll("\n", "⏎")} />
  ) : (
    "Empty"
  );

  if (isEditing) {
    return (
      <>
        <div className="relative w-full">
          <input
            ref={inputRef}
            className="w-full p-2 pr-16 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
          />
          <button
            type="button"
            title={`Open multiline editor (${shortcutLabel})`}
            aria-label="Open multiline editor"
            className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-1 px-1.5 py-0.5 text-xs text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded focus:outline-none"
            onMouseDown={(e) => {
              e.preventDefault();
              openPopup();
            }}
          >
            <span className="font-mono">{shortcutLabel}</span>
            <FontAwesomeIcon icon={faExpandAlt} className="w-3 h-3" />
          </button>
        </div>
        {showPopup && (
          <FullTextEditPopup
            text={text}
            setText={setText}
            onSubmit={(e) => {
              if (e && e.preventDefault) e.preventDefault();
              save();
            }}
            onCancel={cancel}
          />
        )}
      </>
    );
  }

  if (!editable) {
    return (
      <div
        className="flex items-center w-full h-full p-2 truncate text-gray-500 cursor-not-allowed"
        aria-disabled="true"
      >
        {preview}
      </div>
    );
  }

  return (
    <div
      ref={readOnlyRef}
      role="button"
      tabIndex={0}
      className={classNames({
        "flex items-center w-full h-full p-2 truncate focus:outline-none focus:ring-2 focus:ring-primary": true,
        "text-red-500 italic": value === "",
      })}
      onClick={() => setIsEditing(true)}
      onFocus={() => setIsEditing(true)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          setIsEditing(true);
        }
      }}
    >
      {preview}
    </div>
  );
};

export default TranslationInput;
